"use client";

/* eslint-disable @next/next/no-img-element */
/* eslint-disable react/no-unescaped-entities */

import { Author } from '@/app/actions/authors';
import { CaseStudy } from '@/app/actions/case-studies';
import { DynamicIcon } from "@/components/ui/DynamicIcon";
import { FadeIn } from "@/components/ui/motion";
import { Globe, Paintbrush } from 'lucide-react';
import { FaAndroid, FaApple } from 'react-icons/fa';
import ReactMarkdown from 'react-markdown';

interface CaseStudyContentProps {
    project: CaseStudy;
    teamMembers?: { author: Author; role: string }[];
}

export default function CaseStudyContent({ project, teamMembers }: CaseStudyContentProps) {
    return (
        <article className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 pb-0">
            <div className="max-w-4xl mx-auto">
                <FadeIn>
                    <header className="mb-12">
                        <div className="flex flex-wrap gap-2 mb-6">
                            <span className="bg-black text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                                {project.category}
                            </span>
                            {project.tags?.map(tag => (
                                <span key={tag} className="bg-gray-100 text-gray-600 px-3 py-1 rounded-full text-xs font-medium">
                                    {tag}
                                </span>
                            ))}
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-8 leading-tight">
                            {project.title}
                        </h1>
                        <p className="text-xl text-gray-500 leading-relaxed mb-8">
                            {project.excerpt || project.description}
                        </p>

                        <div className="flex flex-wrap gap-4">
                            {project.websiteLink && (
                                <a href={project.websiteLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors">
                                    <Globe className="w-4 h-4" /> Website
                                </a>
                            )}
                            {project.appStoreLink && (
                                <a href={project.appStoreLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors">
                                    <FaApple className="w-4 h-4" /> App Store
                                </a>
                            )}
                            {project.playStoreLink && (
                                <a href={project.playStoreLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors">
                                    <FaAndroid className="w-4 h-4" /> Play Store
                                </a>
                            )}
                            {project.uiDesignLink && (
                                <a href={project.uiDesignLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-full text-sm font-medium transition-colors">
                                    <Paintbrush className="w-4 h-4" /> UI Design
                                </a>
                            )}
                        </div>
                    </header>
                </FadeIn>

                {project.coverImage && (
                    <FadeIn>
                        <div className="mb-16 rounded-2xl overflow-hidden bg-gray-50 shadow-lg flex justify-center">
                            <img
                                src={project.coverImage}
                                alt={project.title}
                                className="max-w-full h-auto rounded-2xl"
                            />
                        </div>
                    </FadeIn>
                )}

                {/* Problem Statement & Solution */}
                {(project.problemStatement || project.solution) && (
                    <FadeIn>
                        <div className="flex flex-col gap-8 mb-16">
                            {project.problemStatement && (
                                <div className="bg-gray-50 rounded-2xl p-8">
                                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                        <span className="w-2 h-8 bg-red-500 rounded-full" />
                                        Problem Statement
                                    </h2>
                                    <div className="prose max-w-none text-gray-600">
                                        <ReactMarkdown>{project.problemStatement}</ReactMarkdown>
                                    </div>
                                </div>
                            )}
                            {project.solution && (
                                <div className="bg-gray-50 rounded-2xl p-8">
                                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                                        <span className="w-2 h-8 bg-green-500 rounded-full" />
                                        Solution
                                    </h2>
                                    <div className="prose max-w-none text-gray-600">
                                        <ReactMarkdown>{project.solution}</ReactMarkdown>
                                    </div>
                                </div>
                            )}
                        </div>
                    </FadeIn>
                )}

                {/* Result Metrics */}
                {project.metrics && project.metrics.length > 0 && (
                    <FadeIn>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
                            {project.metrics.map((metric, index) => (
                                <div key={index} className="bg-gray-50 p-6 rounded-2xl border border-gray-100 flex flex-col items-center text-center space-y-2 hover:shadow-lg transition-shadow">
                                    <div className="bg-white p-3 rounded-xl shadow-sm mb-2 text-black">
                                        <DynamicIcon name={metric.icon} className="w-6 h-6" />
                                    </div>
                                    <p className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600">
                                        {metric.value}
                                    </p>
                                    <p className="text-sm font-medium text-gray-500 uppercase tracking-widest">
                                        {metric.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </FadeIn>
                )}

                {/* Tech Stack */}
                {project.techStack && project.techStack.length > 0 && (
                    <FadeIn>
                        <div className="mb-16">
                            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                <span className="w-2 h-8 bg-blue-500 rounded-full" />
                                Tech Stack
                            </h3>
                            <div className="flex flex-wrap gap-4">
                                {project.techStack.map((tech, index) => (
                                    <div key={index} className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-full border border-gray-100 hover:border-gray-200 transition-colors">
                                        <div className="w-5 h-5 flex items-center justify-center text-gray-600 relative">
                                            <DynamicIcon name={tech.icon} className="w-full h-full" />
                                        </div>
                                        <span className="text-sm font-medium text-gray-700">{tech.name}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FadeIn>
                )}

                {/* Timeline */}
                {project.timeline && project.timeline.length > 0 && (
                    <FadeIn>
                        <div className="mb-16">
                            <h3 className="text-xl font-bold mb-8 flex items-center gap-2">
                                <span className="w-2 h-8 bg-blue-500 rounded-full" />
                                Project Timeline
                            </h3>
                            <div className="relative border-l-2 border-gray-100 ml-3 space-y-12 pb-4">
                                {project.timeline.map((event, index) => (
                                    <div key={index} className="relative pl-12 group">
                                        {/* Dot */}
                                        <div className="absolute left-[-9px] top-1 w-4 h-4 rounded-full bg-white border-4 border-gray-200 group-hover:border-blue-500 transition-colors" />

                                        <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 mb-2">
                                            <h4 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{event.title}</h4>
                                            <span className="text-sm font-medium text-gray-400 bg-gray-50 px-2 py-0.5 rounded-md">{event.date}</span>
                                        </div>
                                        <p className="text-gray-600 leading-relaxed max-w-2xl">
                                            {event.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FadeIn>
                )}

                {/* Team Credits */}
                {teamMembers && teamMembers.length > 0 && (
                    <FadeIn>
                        <div className="mb-16">
                            <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
                                <span className="w-2 h-8 bg-blue-500 rounded-full" />
                                Created By
                            </h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                                {teamMembers.map(({ author, role }) => (
                                    <div key={author.id} className="flex items-center gap-4 p-4 rounded-2xl border border-gray-100 bg-gray-50/50 hover:bg-white hover:shadow-md transition-all">
                                        {author.photo ? (
                                            <img src={author.photo} alt={author.name} className="w-12 h-12 rounded-full object-cover bg-gray-200" />
                                        ) : (
                                            <div className="w-12 h-12 rounded-full bg-gray-200 flex items-center justify-center text-sm font-bold text-gray-500">
                                                {author.name.substring(0, 2).toUpperCase()}
                                            </div>
                                        )}
                                        <div>
                                            <p className="font-bold text-gray-900">{author.name}</p>
                                            <p className="text-xs font-medium text-gray-500 mb-0.5">{role}</p>
                                            {author.link && (
                                                <a href={author.link} target="_blank" rel="noopener noreferrer" className="text-xs font-medium text-blue-600 hover:text-blue-700 hover:underline">
                                                    View Profile
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </FadeIn>
                )}

                {/* Testimonial */}
                {project.testimonial && project.testimonial.quote && (
                    <FadeIn>
                        <div className="mb-16 bg-gray-900 text-white rounded-3xl p-8 md:p-12 relative overflow-hidden">
                            {/* Decorative element */}
                            <div className="absolute top-0 right-0 p-16 bg-white/5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2" />

                            <div className="relative z-10 flex flex-col md:flex-row gap-8 items-center md:items-start">
                                <div className="text-4xl text-gray-600 font-serif">"</div>
                                <div className="flex-1 space-y-6 text-center md:text-left">
                                    <p className="text-xl md:text-2xl font-light italic leading-relaxed text-gray-200">
                                        {project.testimonial.quote}
                                    </p>
                                    <div className="flex items-center gap-4 justify-center md:justify-start">
                                        {project.testimonial.avatar && (
                                            <img
                                                src={project.testimonial.avatar}
                                                alt={project.testimonial.author}
                                                className="w-12 h-12 rounded-full object-cover border-2 border-white/10"
                                            />
                                        )}
                                        <div>
                                            <p className="font-bold text-white">{project.testimonial.author}</p>
                                            <p className="text-sm text-gray-400">{project.testimonial.role}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </FadeIn>
                )}

                <div className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-a:text-blue-600 prose-img:rounded-xl">
                    {/* Render sections if available, otherwise description */}
                    {project.sections && project.sections.length > 0 ? (
                        project.sections.map((section) => (
                            <div key={section.id} className="mb-8">
                                {section.type === 'header' && <h2>{section.content}</h2>}
                                {section.type === 'text' && <ReactMarkdown>{section.content}</ReactMarkdown>}
                                {section.type === 'media' && (
                                    <div className="rounded-xl overflow-hidden my-6 bg-gray-50 flex justify-center">
                                        <img
                                            src={section.content}
                                            alt="Case Study Content"
                                            className="max-w-full h-auto rounded-xl"
                                        />
                                    </div>
                                )}
                                {section.type === 'video' && (
                                    <div className="rounded-xl overflow-hidden my-6 bg-gray-50 flex justify-center">
                                        <video
                                            src={section.content}
                                            controls
                                            className="max-w-full h-auto rounded-xl"
                                        />
                                    </div>
                                )}
                                {section.type === 'divider' && <hr className="my-12 border-gray-100" />}
                            </div>
                        ))
                    ) : (
                        <ReactMarkdown>{project.description || ""}</ReactMarkdown>
                    )}
                </div>
            </div>
        </article>
    );
}